import { Dispatch, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@unified-checkout/design-system';
import { ChevronLeft, ChevronRight } from '@unified-checkout/design-system/icons/index';

import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import { OUTER_ROUTES } from '@app/dashboard/routes/outer-routes';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import { addToLocalStorage, deleteFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { passwordsComparisonLiteral, validateField as fieldValidator } from '@app/dashboard/utils/formValidations';
import { BlueCheckMarkCircular } from "@app/dashboard/assets/icons";
import FinalStepInfo from '@app/dashboard/components/outer_layout/FinalStepInfo';
import FormIntro from '@app/dashboard/components/outer_layout/FormIntro';
import FormIntroHeading from '@app/dashboard/components/outer_layout/FormIntroHeading';
import PhoneNumberVerification from './steps/PhoneNumberVerification';
import RegisterForm from './steps/RegisterForm';
import OtpStep from './steps/OtpStep';


const STEPS = {
  PHONE_NUMBER_VERIFICATION: 1,
  OTP: 2,
  REGISTER: 3,
  SUCCESSFUL_REGISTER: 4,
}

const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.PHONE_NUMBER_VERIFICATION);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [leadToken, setLeadToken] = useState('');

  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('leads')) {
      const pathSegments = path.split("/");
      const leadToken = pathSegments[pathSegments.length - 1];

      setLeadToken(leadToken);
    }
  }, []);

  useEffect(() => {
    const searchParams = window.location.search;
    const flash = new URLSearchParams(searchParams).get("flash");

    const { FLASH_REDIRECT } = LOCAL_STORAGE_KEYS;

    flash === "true" ?
      addToLocalStorage(FLASH_REDIRECT, "true")
      :
      deleteFromLocalStorage(FLASH_REDIRECT);
  }, []);

  const formSubTitles = {
    [STEPS.PHONE_NUMBER_VERIFICATION]: t('outerLayout.form.register.createUrAcc'),
    [STEPS.OTP]: t('form.enterTheCode').replace('{{phoneNum}}', phoneNumber),
    [STEPS.REGISTER]: t('outerLayout.form.register.fillBelowFields'),
    [STEPS.SUCCESSFUL_REGISTER]: '',
  }

  const validateField = (fields: IField, setFields: Dispatch<(prevState: IField) => IField>, fieldName: string, value: string) => {

    let valueToValidate = value;

    if (fieldName === 'confirm_password') {
      const passwordValue = fields["password"].value as string;
      const confirmPasswordValue = fields["confirm_password"].value as string;

      const passwordsLiteral = passwordsComparisonLiteral(passwordValue, confirmPasswordValue);
      valueToValidate = passwordsLiteral;
    };

    fieldValidator(fields, setFields, fieldName, valueToValidate);
  };

  const redirectToLogin = () => {
    navigate(OUTER_ROUTES.LOGIN);
  };

  const replacedServerErrMsg = (text: string): any => {
    const signInLink = <Button
      size='sm'
      color='primary'
      variant='link'
      text={t('outerLayout.form.register.signIn')}
      onClickHandler={redirectToLogin}
    />;

    return text.split("{{loginLink}}").map((part: string, index: number) => (
      index === 0 ? part : signInLink
    ));
  };

  return (
    <FormIntro
      heading={
        <FormIntroHeading
          lead={t('outerLayout.welcomeHeadingPrefix')}
          emphasis={t('outerLayout.welcomeHeadingBrand')}
        />
      }
      renderUsps={true}
      formLegend={t('outerLayout.form.register.signUp')}
      subtitle={formSubTitles[currentStep]}
      backBtnProps={
        currentStep === STEPS.OTP ?
          {
            size: 'md',
            variant: 'outlined',
            leadingIcon: lang === 'en' ? ChevronLeft : ChevronRight,
            onClickHandler: () => setCurrentStep(STEPS.PHONE_NUMBER_VERIFICATION)
          }
          :
          undefined
      }
    >

      {
        currentStep === STEPS.PHONE_NUMBER_VERIFICATION &&
        <PhoneNumberVerification
          setPhoneNumber={setPhoneNumber}
          validateField={validateField}
          loading={loading}
          setLoading={setLoading}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          redirectToLogin={redirectToLogin}
          replacedServerErrMsg={replacedServerErrMsg}
          leadToken={leadToken}
        />
      }
      {
        currentStep === STEPS.OTP &&
        <OtpStep
          loading={loading}
          setLoading={setLoading}
          redirectToLogin={redirectToLogin}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          phoneNumber={phoneNumber}
          replacedServerErrMsg={replacedServerErrMsg}
          leadToken={leadToken}
        />
      }
      {
        currentStep === STEPS.REGISTER &&
        <RegisterForm
          validateField={validateField}
          loading={loading}
          setLoading={setLoading}
          redirectToLogin={redirectToLogin}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          leadToken={leadToken}
        />
      }
      {
        currentStep === STEPS.SUCCESSFUL_REGISTER &&
        <FinalStepInfo
          text={t('outerLayout.form.register.thanksForChoosingPaymob')}
          img={BlueCheckMarkCircular}
        />
      }

    </FormIntro>
  )
}

export default Register;